
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { verifyAdminAccess } from "@/services/user";
import { supabase } from "@/integrations/supabase/client";
import { Shield, ArrowLeft } from "lucide-react";
import StatusBadge from "@/components/admin/verification/StatusBadge";
import UserInfoSection from "@/components/admin/verification/UserInfoSection";
import AdminCriteriaSection from "@/components/admin/verification/AdminCriteriaSection";
import MfaStatusSection from "@/components/admin/verification/MfaStatusSection";
import DevToolsSection from "@/components/admin/verification/DevToolsSection";

const AdminVerificationPage: React.FC = () => {
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError("You need to be logged in to verify admin status");
          return;
        }

        setUser(session.user);
        
        // Run verification
        const result = await verifyAdminAccess();
        setVerificationResult(result);
      } catch (err: any) {
        console.error("Error verifying admin status:", err);
        setError(err.message || "An error occurred during verification");
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  const handleSetDevBypass = () => {
    const isDev = process.env.NODE_ENV === 'development' || 
                 window.location.hostname === 'localhost' || 
                 window.location.hostname === '127.0.0.1';
                 
    if (isDev) {
      localStorage.setItem('admin_bypass', 'true');
      window.location.reload();
    }
  };

  const handleRemoveDevBypass = () => {
    localStorage.removeItem('admin_bypass');
    window.location.reload();
  };

  const isDev = process.env.NODE_ENV === 'development' || 
               window.location.hostname === 'localhost' || 
               window.location.hostname === '127.0.0.1';
  const hasBypass = localStorage.getItem('admin_bypass') === 'true';

  return (
    <Layout>
      <div className="container mx-auto py-8 max-w-2xl">
        <div className="mb-6">
          <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Shield className="h-6 w-6 text-techne-purple mr-2" />
                <CardTitle>Admin Verification Tool</CardTitle>
              </div>
            </div>
            <CardDescription>
              This tool helps diagnose issues with admin access privileges
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-12 w-12 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin"></div>
              </div>
            ) : error ? (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-6">
                <UserInfoSection user={user} verificationResult={verificationResult} />
                <AdminCriteriaSection verificationResult={verificationResult} />
                <MfaStatusSection verificationResult={verificationResult} />
                
                {isDev && (
                  <DevToolsSection 
                    hasBypass={hasBypass} 
                    onSetDevBypass={handleSetDevBypass}
                    onRemoveDevBypass={handleRemoveDevBypass}
                  />
                )}
              </div>
            )}
          </CardContent>
          <CardFooter className="bg-gray-50 border-t flex justify-between">
            <div className="text-sm text-gray-500">
              If you're having trouble with admin access, please contact support.
            </div>
            <Link to="/admin/dashboard">
              <Button variant="outline">Go to Admin Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminVerificationPage;
